import React, { useEffect, useRef, useState } from 'react';
import api from '../api';

const card = { background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)' };
const muted = { color: '#64748b', fontSize: '12px' };

function money(v) {
  return Number(v || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export default function ImportCenterPage() {
  const [imports, setImports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewBank, setPreviewBank] = useState('auto');
  const [previewBusy, setPreviewBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => { loadImports(); }, []);

  async function loadImports() {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/imports');
      setImports(r.data);
      if (!selected && r.data[0]) selectImport(r.data[0]);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Imports konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }

  async function selectImport(imp) {
    setSelected(imp);
    setDetails(null);
    setError('');
    try {
      const r = await api.get(`/imports/${imp.id}/details`);
      setDetails(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Importdetails konnten nicht geladen werden');
    }
  }

  async function rollback() {
    if (!selected || !window.confirm(`Import "${selected.dateiname}" wirklich zurückrollen? Die zugehörigen Buchungen werden gelöscht.`)) return;
    setError('');
    setMessage('');
    try {
      const r = await api.delete(`/imports/${selected.id}`);
      setMessage(`${r.data.geloescht} Buchungen wurden zurückgerollt.`);
      setSelected(null);
      setDetails(null);
      await loadImports();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Rollback fehlgeschlagen');
    }
  }

  async function runPreview(file) {
    if (!file) return;
    setPreviewBusy(true);
    setPreview(null);
    setError('');
    try {
      const form = new FormData();
      form.append('datei', file);
      form.append('bank', previewBank);
      const r = await api.post('/imports/preview', form);
      setPreview(r.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Preview fehlgeschlagen');
    } finally {
      setPreviewBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  const exportSuffix = details?.import?.import_id ? `?import_id=${encodeURIComponent(details.import.import_id)}` : '';

  async function downloadExport(format) {
    try {
      const r = await api.get(`/export/buchungen.${format}${exportSuffix}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `finanzanalyse-buchungen.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Export fehlgeschlagen');
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '14px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: '22px' }}>Import- und Datei-Center</h1>
        <p style={{ ...muted, margin: '4px 0 0' }}>Imports prüfen, Vorschau testen, exportieren und neue Imports sicher zurückrollen.</p>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px', fontWeight: 700 }}>{message}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 0.9fr) minmax(0, 1.2fr)', gap: '12px' }}>
        <div>
          <div style={{ ...card, marginBottom: '12px' }}>
            <h2 style={{ margin: '0 0 10px', fontSize: '16px', color: '#0f172a' }}>Parser-Vorschau</h2>
            <select value={previewBank} onChange={e => setPreviewBank(e.target.value)} style={inputStyle}>
              <option value="auto">Automatisch</option>
              <option value="ING">ING</option>
              <option value="Commerzbank">Commerzbank</option>
              <option value="C24">C24</option>
            </select>
            <input ref={fileRef} type="file" accept=".csv,.CSV" onChange={e => runPreview(e.target.files?.[0])} style={{ ...inputStyle, marginTop: '8px' }} />
            <div style={{ ...muted, marginTop: '8px' }}>{previewBusy ? 'Preview läuft...' : 'CSV prüfen ohne Speichern. PDF-Preview folgt später.'}</div>
            {preview && (
              <div style={{ marginTop: '12px', background: '#f8fafc', borderRadius: '8px', padding: '10px' }}>
                <strong style={{ color: '#0f172a' }}>{preview.datei}</strong>
                <div style={muted}>{preview.gesamt} erkannt ?? {preview.neue} neu ?? {preview.duplikate} Duplikate</div>
              </div>
            )}
          </div>

          <div style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', color: '#0f172a' }}>Import-Historie</h2>
              <button onClick={loadImports} style={buttonStyle('#0f172a')}>Neu laden</button>
            </div>
            {loading && <div style={muted}>Lädt...</div>}
            {!loading && imports.length === 0 && <div style={muted}>Noch keine Imports.</div>}
            {imports.map(imp => (
              <button key={imp.id} onClick={() => selectImport(imp)}
                style={{
                  width: '100%', textAlign: 'left', border: '1px solid ' + (selected?.id === imp.id ? '#2563eb' : '#e2e8f0'),
                  background: selected?.id === imp.id ? '#eff6ff' : 'white', borderRadius: '9px', padding: '10px', marginBottom: '8px', cursor: 'pointer'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                  <strong style={{ color: '#0f172a', fontSize: '13px' }}>{imp.dateiname || 'Import'}</strong>
                  <span style={{ color: '#16a34a', fontWeight: 900 }}>{imp.anzahl}</span>
                </div>
                <div style={{ ...muted, marginTop: '2px' }}>{imp.bank || 'Unbekannt'} ?? {imp.von_datum || '?'} bis {imp.bis_datum || '?'}</div>
                <div style={{ ...muted, marginTop: '2px' }}>{imp.import_id ? 'Rollback möglich' : 'Alt-Import ohne sichere Rollback-ID'}</div>
              </button>
            ))}
          </div>
        </div>

        <div style={card}>
          {!selected && <div style={muted}>Wähle links einen Import aus.</div>}
          {selected && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h2 style={{ margin: 0, color: '#0f172a', fontSize: '17px' }}>{selected.dateiname || 'Import'}</h2>
                  <div style={muted}>{selected.bank || 'Unbekannt'} ?? {selected.von_datum || '?'} bis {selected.bis_datum || '?'}</div>
                </div>
                <span style={{ background: selected.import_id ? '#dcfce7' : '#fef3c7', color: selected.import_id ? '#166534' : '#92400e', borderRadius: '999px', padding: '4px 8px', fontSize: '11px', fontWeight: 900 }}>
                  {selected.import_id ? 'sicher' : 'alt'}
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '12px' }}>
                <MiniStat label="Buchungen" value={details?.buchungen?.length ?? selected.buchungen_aktuell ?? selected.anzahl ?? 0} />
                <MiniStat label="Duplikate" value={selected.duplikate ?? 0} />
                <MiniStat label="Summe" value={money(details?.summe || 0)} />
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <button onClick={() => downloadExport('csv')} style={buttonStyle('#2563eb')}>CSV Export</button>
                <button onClick={() => downloadExport('json')} style={buttonStyle('#0f172a')}>JSON Export</button>
                <button onClick={rollback} disabled={!details?.rollbackMoeglich} style={buttonStyle(details?.rollbackMoeglich ? '#dc2626' : '#94a3b8')}>Rollback</button>
              </div>

              {!details?.rollbackMoeglich && <div style={{ background: '#fef3c7', color: '#92400e', borderRadius: '8px', padding: '10px', marginBottom: '12px', fontSize: '12px', fontWeight: 700 }}>Dieser alte Import hat keine import_id. Automatischer Rollback ist deshalb absichtlich gesperrt.</div>}

              <h3 style={{ margin: '0 0 8px', color: '#0f172a', fontSize: '14px' }}>Buchungen</h3>
              <div style={{ maxHeight: '520px', overflow: 'auto' }}>
                {details?.buchungen?.slice(0, 120).map(b => (
                  <div key={b.id} style={{ borderTop: '1px solid #f1f5f9', padding: '8px 0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                      <strong style={{ color: '#0f172a', fontSize: '13px' }}>{b.empfaenger || b.verwendungszweck || 'Buchung'}</strong>
                      <span style={{ color: b.betrag >= 0 ? '#16a34a' : '#dc2626', fontWeight: 900 }}>{money(b.betrag)}</span>
                    </div>
                    <div style={muted}>{b.datum} ?? {b.kategorie || 'ohne Kategorie'} ?? {b.bank || 'ohne Bank'}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '10px' }}><div style={muted}>{label}</div><div style={{ color: '#0f172a', fontWeight: 900, fontSize: '16px', marginTop: '3px' }}>{value}</div></div>;
}

function buttonStyle(bg) {
  return { background: bg, color: 'white', border: 'none', borderRadius: '9px', padding: '9px 11px', cursor: 'pointer', fontWeight: 900, fontSize: '12px', textDecoration: 'none', display: 'inline-block' };
}

const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '9px', border: '1px solid #cbd5e1', background: 'white' };
